import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Phone } from 'lucide-react';
import { useT } from '../lib/i18n';
import { normalizeRichText } from '../lib/richText';
import { BACKEND_URL as API } from '../lib/config';

// ─────────────────────────────────────────────────────────────────────────
// ModernAbout — the «Moderno» theme About section (s139, approved by Anthony:
// "Overlap Card"). Wide photo with a white card riding over it: label, title,
// description, button, signature and phone; the first stored stat as a badge on
// the photo. Colours come only from theme tokens.
// ─────────────────────────────────────────────────────────────────────────

const SERIF = "'Playfair Display', Georgia, serif";
const img = (src) => (src && src.startsWith('/api') ? `${API}${src}` : src);

const CSS = `
.ma-reveal{opacity:0;transform:translateY(16px);transition:opacity 600ms cubic-bezier(0.23,1,0.32,1),transform 600ms cubic-bezier(0.23,1,0.32,1)}
.ma-reveal.ma-in{opacity:1;transform:none}
@media (prefers-reduced-motion:reduce){.ma-reveal{transform:none;transition:opacity 300ms ease}}
.ma-cta{display:inline-flex;align-items:center;gap:.55rem;font-weight:600;font-size:.875rem;letter-spacing:.02em;padding:.95rem 1.6rem;border-radius:2px;background:var(--color-accent,#ff9c1b);color:var(--accent-ink,#0f172a);transition:transform 160ms ease-out,background-color 220ms ease,opacity 200ms ease}
.ma-cta:active{transform:scale(.97)}
.ma-cta:focus-visible{outline:2px solid var(--color-heading,#1a2332);outline-offset:3px}
@media (hover:hover) and (pointer:fine){.ma-cta:hover .ma-arrow{transform:translateX(3px)}}
.ma-arrow{transition:transform 200ms cubic-bezier(0.23,1,0.32,1)}
.ma-body.rich-text-content h2,.ma-body.rich-text-content h3{font-family:${SERIF};color:var(--color-heading,#1a2332);font-size:1.3rem;line-height:1.3;margin:0 0 .75rem}
.ma-body.rich-text-content p{margin:0 0 .9rem}
.ma-body.rich-text-content p:empty{display:none}
`;

function useReveal() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setInView(true); return undefined; }
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

// Title: last word in italic accent (same device as the hero titles).
function Title({ text }) {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return <>{text}</>;
  const last = words.pop();
  return <>{words.join(' ')} <em style={{ fontStyle: 'italic', fontWeight: 500, color: 'var(--accent-strong, #b86500)' }}>{last}</em></>;
}

function OverlapCard({ data }) {
  const tt = useT();
  const [ref, inView] = useReveal();
  const label = tt(data.label);
  const title = tt(data.title);
  const desc = tt(data.description) ? normalizeRichText(tt(data.description)).replace(/&nbsp;| /g, ' ') : '';
  const btnText = tt(data.button_text);
  const btnUrl = tt(data.button_url) || '#';
  const sigName = tt(data.signature_name);
  const sigTitle = tt(data.signature_title);
  const stat = (Array.isArray(data.stats) ? data.stats : []).find(s => s && (tt(s.value) || tt(s.label)));
  const photo = img(data.image);

  return (
    <section id="about" data-testid="about-section" className="relative overflow-hidden" style={{ background: 'color-mix(in srgb, var(--color-accent, #ff9c1b) 5%, #f6f5f2)' }}>
      <style>{CSS}</style>
      {/* photo: full-bleed left 62% on desktop, top band on mobile */}
      {photo && (
        <div className="relative lg:absolute lg:inset-y-0 lg:left-0 lg:w-[62%] h-[62vw] max-h-[460px] lg:max-h-none lg:h-auto">
          <img src={photo} alt="" className="absolute inset-0 w-full h-full object-cover" />
          {stat && (
            <div className="absolute left-5 bottom-5 lg:left-10 lg:bottom-10 bg-white px-5 py-4 shadow-lg" data-testid="about-stat">
              <div className="text-3xl font-bold leading-none" style={{ fontFamily: SERIF, color: 'var(--color-heading, #1a2332)', fontVariantNumeric: 'tabular-nums' }}>{tt(stat.value)}</div>
              {tt(stat.label) && <div className="mt-1 text-[11px] font-semibold uppercase" style={{ letterSpacing: '.12em', color: 'var(--color-body-text, #64748b)' }}>{tt(stat.label)}</div>}
            </div>
          )}
        </div>
      )}

      <div className="relative max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-[clamp(24px,5vw,80px)] lg:py-24">
        <div ref={ref} className={`ma-reveal ${inView ? 'ma-in' : ''} relative bg-white ${photo ? '-mt-16 lg:mt-0 lg:ml-auto lg:w-[min(560px,46%)]' : 'mx-auto max-w-3xl my-16'} p-7 sm:p-10 lg:p-12 flex flex-col gap-5`}
          style={{ boxShadow: '0 30px 60px -30px rgba(20,26,58,.28)' }}>
          {label && (
            <div className="inline-flex items-center gap-3 text-[11px] font-bold uppercase" style={{ letterSpacing: '.2em', color: 'var(--accent-strong, #b86500)' }}>
              <span style={{ width: 32, height: 2, background: 'var(--color-accent, #ff9c1b)' }} />{label}
            </div>
          )}
          <h2 className="font-bold" style={{ fontFamily: SERIF, fontSize: 'clamp(2rem, 3.4vw, 3rem)', lineHeight: 1.08, color: 'var(--color-heading, #1a2332)', textWrap: 'balance' }} data-testid="about-title">
            <Title text={title} />
          </h2>
          {desc && <div className="ma-body rich-text-content text-base leading-relaxed" style={{ color: 'var(--color-body-text, #475569)' }} dangerouslySetInnerHTML={{ __html: desc }} />}
          {btnText && (
            <div>
              <a href={btnUrl} target={data.button_open_in_new_tab ? '_blank' : '_self'} rel="noopener noreferrer" className="ma-cta mx-btn mx-btn--primary" data-testid="about-cta-btn">
                {btnText} <ArrowRight className="w-4 h-4 ma-arrow" />
              </a>
            </div>
          )}
          {(sigName || data.phone) && (
            <div className="mt-2 pt-5 border-t flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4" style={{ borderColor: 'rgba(20,26,58,.1)' }}>
              {sigName && (
                <div data-testid="about-signature">
                  <div className="text-xl italic" style={{ fontFamily: SERIF, fontWeight: 600, color: 'var(--color-heading, #1a2332)' }}>{sigName}</div>
                  {sigTitle && <div className="text-[11px] font-semibold uppercase" style={{ letterSpacing: '.14em', color: 'var(--color-body-text, #64748b)' }}>{sigTitle}</div>}
                </div>
              )}
              {data.phone && (
                <a href={`tel:${String(data.phone).replace(/[^+\d]/g, '')}`} className="flex items-center gap-3 group" data-testid="about-phone">
                  <span className="grid place-items-center w-10 h-10 rounded-full border-[1.5px]" style={{ borderColor: 'var(--color-accent, #ff9c1b)', color: 'var(--accent-strong, #b86500)' }}><Phone className="w-4 h-4" /></span>
                  <span>
                    <span className="block text-[10px] font-semibold uppercase" style={{ letterSpacing: '.12em', color: 'var(--color-body-text, #64748b)' }}>Call us</span>
                    <span className="block font-semibold" style={{ color: 'var(--color-heading, #1a2332)', fontVariantNumeric: 'tabular-nums' }}>{data.phone}</span>
                  </span>
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default function ModernAbout({ data }) {
  const tt = useT();
  if (!data || !tt(data.title)) return null;
  return <OverlapCard data={data} />;
}
